import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "./cityPricing";

const toulon: ChauffeurPage = {
  slug: "toulon",
  title: "Chauffeur privé & VTC à Toulon",
  subtitle:
    "Chauffeur privé à Toulon : gare TGV, port et rade, Mont Faron et transferts vers l'aéroport de Toulon-Hyères et de Nice.",
  keywords: [
    "chauffeur privé toulon",
    "vtc toulon",
    "transfert gare toulon",
    "chauffeur toulon aéroport",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Toulon",
    subtitle:
      "La rade, le port militaire, la gare TGV et le Mont Faron : un chauffeur pour la ville et tout le Var, 24h/24.",
    keywords: [
      "VTC Toulon",
      "chauffeur gare de Toulon",
      "transfert aéroport Toulon-Hyères",
      "chauffeur rade de Toulon",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Toulon",
    badges: [
      "Gare TGV de Toulon",
      "Aéroport Toulon-Hyères ~30 min",
      "Chauffeurs FR/EN",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Toulon",
    items: [
      {
        title: "Gare TGV au cœur de la ville",
        description:
          "Prise en charge à la gare de Toulon, TGV Paris et correspondances, avec accueil quai ou parvis.",
      },
      {
        title: "Deux aéroports desservis",
        description:
          "Toulon-Hyères à environ 30 min, Nice Côte d'Azur à près de 1h30 : le choix se fait selon votre vol.",
      },
      {
        title: "Rade & port",
        description:
          "Dépose au port, aux gares maritimes des ferries pour la Corse et aux points de la rade, sans parking.",
      },
      {
        title: "Service noté 5,0/5",
        description:
          "Chauffeurs discrets et ponctuels, basés sur la Côte d'Azur, notés 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Toulon",
    services: [
      {
        title: "Transfert gare TGV de Toulon",
        text: "Accueil à la gare de Toulon pour les TGV Paris et les correspondances, départ calé sur votre train.",
      },
      {
        title: "Transfert aéroport de Toulon-Hyères",
        text: "L'aéroport le plus proche, à environ 30 min, avec suivi de vol depuis et vers Toulon.",
      },
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison vers Nice Côte d'Azur en près de 1h30 par l'A57, en berline ou van premium.",
      },
      {
        title: "Port & ferries de la rade",
        text: "Dépose aux gares maritimes pour la Corse et la Sardaigne, et aux quais de la rade de Toulon.",
      },
      {
        title: "Mise à disposition & longue distance",
        text: "Chauffeur à la journée pour la ville et le Var, ou trajets vers Saint-Tropez, Cannes et l'Italie.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Gare de Toulon",
        description: "Gare TGV et parvis, au centre de la ville.",
      },
      {
        name: "Port & rade de Toulon",
        description: "Quais, gares maritimes et front de mer.",
      },
      {
        name: "Téléphérique du Mont Faron",
        description: "Départ du téléphérique et belvédère sur la rade.",
      },
      {
        name: "Opéra & cours Lafayette",
        description: "Opéra de Toulon et marché provençal du cours Lafayette.",
      },
      {
        name: "Aéroport de Toulon-Hyères",
        description: "L'aéroport le plus proche, à environ 30 min.",
      },
      {
        name: "Le Mourillon",
        description: "Plages et quartier maritime de la ville.",
      },
    ],
  },
  generic: {
    heading: "Toulon, la grande rade et sa gare TGV",
    subheading:
      "Une ville portuaire dense, où la gare, le port et les hauteurs se relient mieux avec un chauffeur.",
    paragraphs: [
      "Toulon s'organise autour de l'une des plus belles rades d'Europe et de son port militaire, première base navale française. La ville est dense : entre la gare, le port, l'Opéra, le marché du cours Lafayette et les plages du Mourillon, un chauffeur privé vous conduit de porte à porte et vous épargne le stationnement compliqué du centre.",
      "La gare de Toulon met Paris à portée de TGV : nous vous y accueillons, quai ou parvis, et calons le départ sur votre train. Côté avion, l'aéroport de Toulon-Hyères est le plus proche, à environ 30 minutes, tandis que Nice Côte d'Azur se rejoint en près de 1h30 par l'autoroute pour les vols internationaux.",
      "Toulon est aussi un point d'embarquement vers la Corse et la Sardaigne, et le pied du Mont Faron, dont le téléphérique domine la rade. Pour vos journées, la mise à disposition confie un chauffeur dédié à la ville comme au reste du Var, avec des forfaits fixes définis à l'avance et un devis immédiat.",
    ],
    bullets: [
      "Accueil gare TGV",
      "Deux aéroports & ferries",
      "Forfaits fixes & devis immédiat",
    ],
  },
  process: {
    heading: "Réserver un chauffeur à Toulon",
    steps: [
      "Indiquez votre train, votre vol ou votre adresse à Toulon.",
      "Nous conseillons le véhicule selon les bagages, le groupe et la distance.",
      "Le chauffeur suit le train ou le vol et adapte le départ au trafic.",
      "Transfert direct ou mise à disposition pour la ville et la rade.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Toulon"),
    {
      q: "Assurez-vous les transferts depuis la gare TGV de Toulon ?",
      a: "Oui, c'est l'un de nos trajets courants : accueil à la gare de Toulon pour les TGV Paris et les correspondances, avec départ ajusté à l'horaire de votre train.",
    },
    {
      q: "Quel est l'aéroport le plus proche de Toulon ?",
      a: "L'aéroport de Toulon-Hyères, à environ 30 minutes, est le plus proche. Nice Côte d'Azur, plus grand pour les longs-courriers, se situe à près de 1h30 par l'A57.",
    },
    {
      q: "Combien de temps à l'avance faut-il réserver ?",
      a: "Pour un transfert calé sur un TGV ou un vol, réservez la veille de préférence ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, à l'aise avec une clientèle d'affaires et internationale.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes Classe E ou Classe S, van premium 7 places pour les groupes et les bagages, SUV et minibus VIP selon vos besoins.",
    },
    {
      q: "Desservez-vous le port et les ferries pour la Corse ?",
      a: "Oui, nous déposons aux gares maritimes de la rade de Toulon pour les traversées vers la Corse et la Sardaigne, et récupérons à l'arrivée des bateaux.",
    },
    {
      q: "Faites-vous les longues distances depuis Toulon ?",
      a: "Oui : Saint-Tropez, Cannes, Nice, Monaco, Marseille et l'Italie, ainsi que la desserte depuis Cannes ou Nice vers Toulon, en véhicule confortable et chauffeur dédié.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur privé Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    { label: "Service de chauffeur à Nice", href: "/chauffeur/nice" },
    {
      label: "VTC à Cannes",
      href: "/chauffeur/cannes",
    },
    {
      label: "Transferts avec chauffeur à Saint-Tropez",
      href: "/chauffeur/saint-tropez",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Toulon",
    text: "Gare TGV, aéroport, port ou journée dans la rade : réservez votre chauffeur, réponse rapide 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Toulon — VTC & transfert aéroport",
    description:
      "Chauffeur privé et VTC à Toulon : gare TGV, transfert aéroport de Toulon-Hyères (~30 min) et de Nice, port et rade, Mont Faron. 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/toulon",
  },
};

export default toulon;
